import React, { createContext, useState, useContext, useEffect, ReactNode, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import type { UserRole } from '../types/workflow-v9';
import { normalizeRole, type CanonicalRole } from '../permissions';
import { recordAuditEvent } from '../services/auditLogService';
import type { User as SupabaseAuthUser } from '@supabase/supabase-js';

// Re-exportar UserRole para compatibilidade
export type { UserRole };

export type ViewMode = 'auto' | 'admin' | 'gestor' | 'usuario';

export interface AuthMembership {
  organizationId: string;
  organizationName?: string;
  role?: UserRole;
}

const VIEW_MODE_STORAGE_KEY = 'workflow_view_mode';

const getInitialViewMode = (): ViewMode => {
  if (typeof window === 'undefined') return 'auto';
  try {
    const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY) as ViewMode | null;
    return stored === 'admin' || stored === 'gestor' || stored === 'usuario' ? stored : 'auto';
  } catch {
    return 'auto';
  }
};

const canonicalToUserRole = (role: CanonicalRole): UserRole => {
  switch (role) {
    case 'gestor':
      return 'manager';
    case 'usuario':
      return 'operator';
    default:
      return role;
  }
};

// Definição do tipo de Usuário
interface User {
  email: string;
  name: string;
  id?: string;
  role?: UserRole;
  organizationId?: string;
  organization_id?: string;
  organizationName?: string;
  view_mode?: ViewMode;
  avatar_url?: string | null;
}

// O que o contexto vai disponibilizar para o App
interface AuthContextType {
  user: User | null;
  memberships: AuthMembership[];
  login: (userData: User) => void;
  logout: () => void;
  isLoading: boolean;
  signUp: (email: string, password: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  isOperator: boolean;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  effectiveRole: UserRole;
  setActiveOrganizationMembership: (organizationId: string, organizationName?: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [memberships, setMemberships] = useState<AuthMembership[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewModeState] = useState<ViewMode>(getInitialViewMode);
  const viewModeRef = useRef<ViewMode>(viewMode);
  // Tracks the Supabase user ID that is currently hydrated.
  // Used to skip redundant SIGNED_IN events fired on window restore / token refresh.
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  const applyBaseUserState = useCallback((authUser: SupabaseAuthUser) => {
    setUser({
      email: authUser.email || '',
      name: authUser.user_metadata?.name || authUser.email?.split('@')[0] || 'User',
      id: authUser.id,
      organizationId: undefined,
      organization_id: undefined,
      organizationName: undefined,
      role: undefined,
      view_mode: viewModeRef.current,
      avatar_url: (authUser.user_metadata?.avatar_url as string | undefined) ?? null,
    });
    setMemberships([]);
  }, []);

  const hydrateUserFromProfile = useCallback(async (authUser: SupabaseAuthUser) => {
    // Only reset base state when it's a different account (e.g. user switch).
    // For the same user we keep the existing state intact during re-hydration
    // so permission guards don't see a blank role and navigate away.
    if (currentUserIdRef.current !== authUser.id) {
      applyBaseUserState(authUser);
    }
    currentUserIdRef.current = authUser.id;

    // Sincroniza avatar do user_profiles (fonte de verdade persistente)
    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('avatar_url')
        .eq('user_id', authUser.id)
        .maybeSingle();
      const profileAvatar = (profile?.avatar_url as string | null | undefined) ?? null;
      if (profileAvatar) {
        setUser((prev) => (prev && prev.id === authUser.id ? { ...prev, avatar_url: profileAvatar } : prev));
      }
    } catch (err) {
      console.debug('[AuthContext] avatar fetch skipped:', err);
    }

    try {
      const { data: memberships, error } = await supabase
        .from('user_organizations')
        .select('organization_id, role, organizations(id, name)')
        .eq('user_id', authUser.id);

      if (error) throw error;

      console.debug('[AuthContext] user_organizations memberships', memberships);

      const normalizedMemberships: AuthMembership[] = (memberships ?? [])
        .map((membership) => {
          const organizationId = membership.organization_id ?? membership.organizations?.id;
          if (!organizationId) return null;

          return {
            organizationId,
            organizationName: membership.organizations?.name,
            role: membership.role as UserRole | undefined,
          };
        })
        .filter((membership): membership is AuthMembership => Boolean(membership));

      setMemberships(normalizedMemberships);

      let preferredOrganizationId: string | null = null;
      try {
        if (typeof window !== 'undefined') {
          preferredOrganizationId = window.localStorage.getItem('lean_office_active_org');
        }
      } catch {
        preferredOrganizationId = null;
      }

      const initialMembership = normalizedMemberships.find(
        (membership) => membership.organizationId === preferredOrganizationId,
      ) ?? normalizedMemberships[0];

      let finalizedUser: User | null = null;

      setUser(prev => {
        if (!prev || prev.id !== authUser.id) return prev;

        const membership = initialMembership;
        if (!membership) {
          console.error('[AuthContext] Nenhuma membership retornada para usuário', authUser.id);
          const fallbackUser = {
            ...prev,
            role: undefined,
          };
          finalizedUser = fallbackUser;
          return fallbackUser;
        }

        const organizationId = membership.organizationId
          ?? prev.organizationId
          ?? prev.organization_id;

        if (!organizationId) {
          console.error('[AuthContext] Membership sem organization_id', membership);
        }

        const nextUser = {
          ...prev,
          organizationId,
          organization_id: organizationId,
          organizationName: membership.organizationName ?? prev.organizationName,
          role: membership.role,
        };

        finalizedUser = nextUser;
        return nextUser;
      });


    } catch (error) {
      console.warn('[AuthContext] Falha ao carregar user_organizations:', error);
      setMemberships([]);
      setUser(prev => {
        if (!prev || prev.id !== authUser.id) return prev;
        return {
          ...prev,
          role: undefined,
        };
      });
    }
  }, [applyBaseUserState]);

  // Carregar usuário do Supabase ao abrir
  useEffect(() => {
    let isMounted = true;

    const loadSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!isMounted) return;

      if (session?.user) {
        await hydrateUserFromProfile(session.user);
      } else {
        setUser(null);
        setMemberships([]);
      }
      setIsLoading(false);
    };

    loadSession();

    // Escutar mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Events that don't change identity: skip entirely to avoid wiping
      // organizationId/role while the DB re-query is in flight (which would
      // cause permission guards to redirect the user away mid-session).
      if (event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
        return;
      }

      // SIGNED_IN for the same already-loaded user is a session re-validation
      // (Supabase fires this on window/Electron restore). Skip re-hydration —
      // the user is already fully loaded and navigation must not be disturbed.
      if (
        event === 'SIGNED_IN' &&
        session?.user &&
        currentUserIdRef.current === session.user.id
      ) {
        return;
      }

      if (session?.user) {
        void hydrateUserFromProfile(session.user);
      } else {
        currentUserIdRef.current = null;
        setUser(null);
        setMemberships([]);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [hydrateUserFromProfile]);

  // Cadastro via Supabase
  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    if (error) throw error;

    if (data.user) {
      await hydrateUserFromProfile(data.user);
    }
  };

  // Login via Supabase
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;

    if (data.user) {
      await hydrateUserFromProfile(data.user);
    }
  };

  // Função de Login (mantida para compatibilidade)
  const login = (userData: User) => {
    setMemberships([]);
    setUser({ ...userData, view_mode: viewMode });
  };

  // Função de Logout
  const logout = async () => {
    await supabase.auth.signOut();
    currentUserIdRef.current = null;
    setUser(null);
    setMemberships([]);
  };

  const setActiveOrganizationMembership = useCallback((organizationId: string, organizationName?: string) => {
    setUser(prev => {
      if (!prev) return prev;

      const membership = memberships.find((item) => item.organizationId === organizationId);

      return {
        ...prev,
        organizationId,
        organization_id: organizationId,
        organizationName: organizationName ?? membership?.organizationName ?? prev.organizationName,
        role: membership?.role,
      };
    });
  }, [memberships]);

  // Verificar se é operador
  const canonicalEffectiveRole = normalizeRole(user?.role ?? null) ?? 'usuario';
  const effectiveRole = canonicalToUserRole(canonicalEffectiveRole as CanonicalRole);
  const isOperator = canonicalEffectiveRole === 'usuario';

  const updateViewMode = (mode: ViewMode) => {
    const previousMode = viewMode;
    setViewModeState(mode);
    setUser(prev => (prev ? { ...prev, view_mode: mode } : prev));
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
      }
    } catch {
      /* ignore */
    }

    if (user?.id) {
      recordAuditEvent({
        userId: user.id,
        organizationId: user.organizationId ?? user.organization_id ?? null,
        realRole: user.role,
        viewMode: mode,
        action: 'view_mode.change',
        metadata: {
          previousMode,
          newMode: mode,
        },
      });
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      memberships,
      login,
      logout,
      isLoading,
      signUp,
      signIn,
      isOperator,
      viewMode,
      setViewMode: updateViewMode,
      effectiveRole,
      setActiveOrganizationMembership,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook personalizado para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
